import React, { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, extractError } from "../lib/api";
import { PageHeader, SectionCard, Pill } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Pencil, Trash2, UserPlus, Shield, Users as UsersIcon, AlertTriangle } from "lucide-react";
import { useAuth, isAdmin } from "../context/AuthContext";
import AdminContractPurge from "../components/AdminContractPurge";
import DataTableBlock, { dataTableListTitle } from "../components/DataTableBlock";
import DataTableExportButton from "../components/DataTableExportButton";
import { useUsersBootstrap } from "../hooks/useBootstrapList";
import { LIST_PAGE_SIZE_OPTIONS } from "../lib/listPagination";
import { USER_COLUMNS, ROLE_COLUMNS } from "./userColumns";

const TABS = [
  { id: "users", label: "Users", icon: UsersIcon },
  { id: "roles", label: "Roles", icon: Shield },
  { id: "purge", label: "Permanent delete", icon: AlertTriangle },
];

export default function UsersPage() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [tab, setTab] = useState("users");
  const [userFormOpen, setUserFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [roleFormOpen, setRoleFormOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [userForm, setUserForm] = useState({
    email: "",
    name: "",
    password: "",
    role: "manager",
    extra_roles: [],
    department: "",
    reports_to_id: "",
    is_active: true,
    tally_username: "",
    tally_password: "",
  });
  const [roleForm, setRoleForm] = useState({ slug: "", name: "", description: "", permissions: [] });
  const { userList, roleList, permissions, reload: reloadUsers } = useUsersBootstrap({
    userColumns: USER_COLUMNS,
    roleColumns: ROLE_COLUMNS,
    userDefaultSort: { id: "name", desc: false },
    roleDefaultSort: { id: "name", desc: false },
    enabled: admin,
  });

  const pagePerms = useMemo(() => permissions.filter((p) => p.category === "page"), [permissions]);
  const featurePerms = useMemo(() => permissions.filter((p) => p.category === "feature"), [permissions]);

  const foremanOptions = useMemo(
    () => userList.rows.filter((u) => String(u.role || "").endsWith("_foreman")),
    [userList.rows],
  );

  if (user && !admin) {
    return <Navigate to="/" replace />;
  }

  const openNewUser = () => {
    setEditingUser(null);
    setUserForm({
      email: "",
      name: "",
      password: "",
      role: "manager",
      extra_roles: [],
      department: "",
      reports_to_id: "",
      is_active: true,
      tally_username: "",
      tally_password: "",
    });
    setUserFormOpen(true);
  };

  const openEditUser = (u) => {
    setEditingUser(u);
    const extras = Array.isArray(u.extra_roles)
      ? u.extra_roles
      : (u.roles || []).filter((r) => r !== u.role);
    setUserForm({
      email: u.email,
      name: u.name,
      password: "",
      role: u.role,
      extra_roles: extras.filter((r) => r && r !== u.role),
      department: u.department || "",
      reports_to_id: u.reports_to_id ? String(u.reports_to_id) : "",
      is_active: u.is_active !== false,
      tally_username: u.tally_username || "",
      tally_password: "",
    });
    setUserFormOpen(true);
  };

  const toggleExtraRole = (slug) => {
    setUserForm((prev) => {
      const has = prev.extra_roles.includes(slug);
      const extra_roles = has
        ? prev.extra_roles.filter((r) => r !== slug)
        : [...prev.extra_roles, slug];
      return { ...prev, extra_roles };
    });
  };

  const saveUser = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: userForm.name,
        email: userForm.email,
        role: userForm.role,
        extra_roles: (userForm.extra_roles || []).filter((r) => r && r !== userForm.role),
        department: userForm.department || null,
        reports_to_id: userForm.reports_to_id ? Number(userForm.reports_to_id) : null,
        is_active: userForm.is_active,
        tally_username: userForm.tally_username || null,
      };
      if (editingUser) {
        if (userForm.password) payload.password = userForm.password;
        if (userForm.tally_password) payload.tally_password = userForm.tally_password;
        await api.patch(`/users/${editingUser.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", {
          ...payload,
          password: userForm.password,
          tally_password: userForm.tally_password || undefined,
        });
        toast.success("User created");
      }
      setUserFormOpen(false);
      setEditingUser(null);
      setUserForm({
        email: "",
        name: "",
        password: "",
        role: "manager",
        extra_roles: [],
        department: "",
        reports_to_id: "",
        is_active: true,
        tally_username: "",
        tally_password: "",
      });
      reloadUsers();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user "${u.name}" (${u.email})?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      reloadUsers();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    setRoleForm({
      slug: role.slug,
      name: role.name,
      description: role.description || "",
      permissions: role.permissions || [],
    });
    setRoleFormOpen(true);
  };

  const openNewRole = () => {
    setEditingRole(null);
    setRoleForm({ slug: "", name: "", description: "", permissions: [] });
    setRoleFormOpen(true);
  };

  const togglePerm = (code) => {
    setRoleForm((f) => ({
      ...f,
      permissions: f.permissions.includes(code)
        ? f.permissions.filter((c) => c !== code)
        : [...f.permissions, code],
    }));
  };

  const saveRole = async (e) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await api.patch(`/rbac/roles/${editingRole.id}`, {
          name: roleForm.name,
          description: roleForm.description,
          permissions: roleForm.permissions,
        });
        toast.success("Role updated");
      } else {
        await api.post("/rbac/roles", roleForm);
        toast.success("Role created");
      }
      setRoleFormOpen(false);
      reloadUsers();
      reloadUsers();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const deleteRole = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      await api.delete(`/rbac/roles/${role.id}`);
      toast.success("Role deleted");
      reloadUsers();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="users-page">
      <PageHeader
        eyebrow="Access Control"
        title="Administration"
        description="Administrator only — manage users, roles, and system access."
        actions={
          tab === "users" ? (
            <Button onClick={openNewUser} className="bg-bansal-sage hover:bg-bansal-sageDark text-white">
              <UserPlus className="h-4 w-4 mr-1" /> New User
            </Button>
          ) : (
            <Button onClick={openNewRole} className="bg-bansal-sage hover:bg-bansal-sageDark text-white">
              <Shield className="h-4 w-4 mr-1" /> New Role
            </Button>
          )
        }
      />

      <div className="flex gap-2 border-b border-border">
        {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${
                tab === t.id ? "border-bansal-sage text-primary" : "border-transparent text-muted-foreground"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
        ))}
      </div>

      {tab === "users" && (
        <>
          {userFormOpen && (
            <SectionCard title={editingUser ? `Edit user: ${editingUser.name}` : "Create user"}>
              <form onSubmit={saveUser} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field label="Full name" required>
                  <Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} required />
                </Field>
                <Field label="Email" required>
                  <Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} required />
                </Field>
                <Field label={editingUser ? "New password (optional)" : "Password"} required={!editingUser}>
                  <Input
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    required={!editingUser}
                    minLength={editingUser && !userForm.password ? undefined : 6}
                    placeholder={editingUser ? "Leave blank to keep current" : ""}
                  />
                </Field>
                <Field label="Tally user ID">
                  <Input
                    value={userForm.tally_username || ""}
                    onChange={(e) => setUserForm({ ...userForm, tally_username: e.target.value })}
                    placeholder="Tally company security username"
                    autoComplete="off"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Used for Tally Edit Log when this user Approves &amp; Syncs. Each accounts user should have their own login.
                  </p>
                </Field>
                <Field
                  label={
                    editingUser?.tally_password_configured
                      ? "Tally password (leave blank to keep)"
                      : "Tally password"
                  }
                >
                  <Input
                    type="password"
                    value={userForm.tally_password || ""}
                    onChange={(e) => setUserForm({ ...userForm, tally_password: e.target.value })}
                    placeholder={editingUser?.tally_password_configured ? "••••••••" : ""}
                    autoComplete="new-password"
                  />
                </Field>
                <Field label="Primary role" required>
                  <Select
                    value={userForm.role}
                    onValueChange={(v) =>
                      setUserForm({
                        ...userForm,
                        role: v,
                        extra_roles: (userForm.extra_roles || []).filter((r) => r !== v),
                      })
                    }
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {roleList.rows.map((r) => (
                        <SelectItem key={r.slug} value={r.slug}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <div className="md:col-span-2 lg:col-span-4 rounded-md border border-border p-3 space-y-2">
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Additional roles
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Optional — e.g. assign Weighbridge + Accounts so one person can use both desks.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {roleList.rows
                      .filter((r) => r.slug !== userForm.role)
                      .map((r) => {
                        const checked = (userForm.extra_roles || []).includes(r.slug);
                        return (
                          <label key={r.slug} className="flex items-center gap-2 min-h-10 text-sm">
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleExtraRole(r.slug)}
                            />
                            <span>{r.name}</span>
                          </label>
                        );
                      })}
                  </div>
                </div>
                <Field label="Department">
                  <Select value={userForm.department || "none"} onValueChange={(v) => setUserForm({ ...userForm, department: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      <SelectItem value="boiler">Boiler</SelectItem>
                      <SelectItem value="parboiled">Parboiled</SelectItem>
                      <SelectItem value="milling">Milling</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Reports to (foreman)">
                  <Select value={userForm.reports_to_id || "none"} onValueChange={(v) => setUserForm({ ...userForm, reports_to_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {foremanOptions.map((f) => (
                        <SelectItem key={f.id} value={String(f.id)}>{f.name} ({f.role})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <label className="flex items-center gap-3 min-h-[44px] md:col-span-2">
                  <Checkbox checked={userForm.is_active} onCheckedChange={(v) => setUserForm({ ...userForm, is_active: v === true })} />
                  <span className="text-sm">Account active</span>
                </label>
                <div className="md:col-span-2 lg:col-span-4 flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => { setUserFormOpen(false); setEditingUser(null); }}>Cancel</Button>
                  <Button type="submit" className="bg-bansal-sage hover:bg-bansal-sageDark text-white">
                    {editingUser ? "Save changes" : "Create user"}
                  </Button>
                </div>
              </form>
            </SectionCard>
          )}

          <DataTableBlock
            title={dataTableListTitle("users", userList.meta.count, {
              filtered: userList.table.rows.length,
              hasFilters: userList.table.hasActiveFilters,
            })}
            columns={USER_COLUMNS}
            table={userList.table}
            loading={userList.loading}
            totalCount={userList.meta.count}
            testId="users-table"
            pagination={{
              ...userList.pagination,
              pageSizeOptions: LIST_PAGE_SIZE_OPTIONS,
            }}
            exportButton={(
              <DataTableExportButton
                apiPath="/users"
                params={userList.exportParams}
                columns={USER_COLUMNS}
                filename="users.xlsx"
                testId="users-export"
              />
            )}
            renderRow={(u) => (
              <tr key={u.id} className="border-t">
                <td className="px-3 py-2.5 font-medium">{u.name}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{u.email}</td>
                <td className="px-3 py-2.5">
                  <div className="flex flex-wrap gap-1">
                    {(u.roles?.length ? u.roles : [u.role]).map((slug) => (
                      <Pill key={slug} tone={slug === u.role ? "sage" : "default"}>
                        {slug}
                      </Pill>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2.5 text-sm">{u.department || "—"}</td>
                <td className="px-3 py-2.5 text-sm">{u.is_active === false ? "Disabled" : "Active"}</td>
                <td className="px-3 py-2.5">
                  <div className="flex justify-end gap-1">
                    <Button type="button" variant="outline" size="sm" onClick={() => openEditUser(u)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-red-600"
                      disabled={String(u.id) === String(user?.id)}
                      title="Delete permanently"
                      onClick={() => deleteUser(u)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            )}
            renderTrailingHeader="Actions"
          />
        </>
      )}

      {tab === "roles" && (
        <>
          {roleFormOpen && (
            <SectionCard title={editingRole ? `Edit role: ${editingRole.name}` : "Create role"}>
              <form onSubmit={saveRole} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {!editingRole && (
                    <Field label="Slug (unique id)" required>
                      <Input
                        value={roleForm.slug}
                        onChange={(e) => setRoleForm({ ...roleForm, slug: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
                        placeholder="e.g. night_guard"
                        required
                        disabled={!!editingRole}
                      />
                    </Field>
                  )}
                  <Field label="Display name" required>
                    <Input value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} required />
                  </Field>
                  <Field label="Description">
                    <Input value={roleForm.description} onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })} />
                  </Field>
                </div>

                <div>
                  <h4 className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Page access</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {pagePerms.map((p) => (
                      <label key={p.code} className="flex items-start gap-2 text-sm border rounded-md p-2 cursor-pointer hover:bg-muted/50">
                        <Checkbox checked={roleForm.permissions.includes(p.code)} onCheckedChange={() => togglePerm(p.code)} />
                        <span>
                          <span className="font-medium block">{p.name}</span>
                          <span className="text-[10px] text-muted-foreground font-mono">{p.code}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Feature access</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                    {featurePerms.map((p) => (
                      <label key={p.code} className="flex items-start gap-2 text-sm border rounded-md p-2 cursor-pointer hover:bg-muted/50">
                        <Checkbox checked={roleForm.permissions.includes(p.code)} onCheckedChange={() => togglePerm(p.code)} />
                        <span>
                          <span className="font-medium block">{p.name}</span>
                          <span className="text-[10px] text-muted-foreground font-mono">{p.code}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setRoleFormOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-bansal-sage hover:bg-bansal-sageDark text-white">
                    {editingRole ? "Save role" : "Create role"}
                  </Button>
                </div>
              </form>
            </SectionCard>
          )}

          <SectionCard
            title={dataTableListTitle("roles", roleList.meta.count, {
              filtered: roleList.table.rows.length,
              hasFilters: roleList.table.hasActiveFilters,
            })}
            action={(
              <DataTableExportButton
                apiPath="/rbac/roles"
                params={roleList.exportParams}
                columns={ROLE_COLUMNS}
                filename="roles.xlsx"
                testId="roles-export"
              />
            )}
          >
            <DataTableBlock
              title=""
              wrapInSectionCard={false}
              columns={ROLE_COLUMNS}
              table={roleList.table}
              loading={roleList.loading}
              totalCount={roleList.meta.count}
              testId="roles-filters"
              showBody={false}
              pagination={{
                ...roleList.pagination,
                pageSizeOptions: LIST_PAGE_SIZE_OPTIONS,
              }}
            />
            <div className="space-y-3 mt-4">
              {roleList.table.rows.map((r) => (
                <div key={r.id} className="border rounded-md p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{r.name}</span>
                      <span className="font-mono text-xs text-muted-foreground">{r.slug}</span>
                      {r.is_system && <Pill tone="gold">System</Pill>}
                      <Pill tone="default">{r.user_count ?? 0} users</Pill>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{r.permissions?.length || 0} permissions · {r.description}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button type="button" variant="outline" size="sm" onClick={() => openEditRole(r)}>Edit permissions</Button>
                    {!r.is_system && (
                      <Button type="button" variant="outline" size="sm" className="text-red-600" onClick={() => deleteRole(r)}>Delete</Button>
                    )}
                  </div>
                </div>
              ))}
              {roleList.table.rows.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">
                  {roleList.table.hasActiveFilters ? "No roles match your filters." : "No roles yet."}
                </p>
              )}
            </div>
          </SectionCard>
        </>
      )}

      {tab === "purge" && <AdminContractPurge />}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
